var Type = require('./Type');

/**
 * The CalendarDate class represents a date/datetime/time data type.
 * It handles the formatting and accepts several options like
 * calendar, a custom format string and a subtype.
 *
 * @param {Object} [oConfig] - A configuration object that is used to specify the formatting
 * @param {string} [oConfig.type] - The specific type for this CalendarDate (i.E. date, datetime, time)
 * @param {string} [oConfig.format] - A custom format string that will be used as template
 * @param {string} [oConfig.calendar] - Specifies the calendar that will be used (default: gregorian)
 * @param {string} [oConfig.inputFormat] - Specifies the format of the string formatted date value (uppercase YYYYMMDD)
 * @constructor
 */
function CalendarDate(oConfig) {
	Type.call(this, oConfig);

	this.type = oConfig && typeof oConfig.type === 'string' ? oConfig.type.toLowerCase() : null;
	this.format = oConfig ? oConfig.format : null;
	this.calendar = oConfig ? oConfig.calendar : null;
	this.inputFormat = oConfig && typeof oConfig.inputFormat == 'string' ? oConfig.inputFormat.toUpperCase() : null;
}

CalendarDate.prototype = Object.create(Type.prototype);
CalendarDate.prototype.constructor = CalendarDate;

CalendarDate.DEFAULT = {
	numberFormat: 14
};

CalendarDate.builtin = {
	14: 'mm-dd-yy',
	15: 'd-mmm-yy',
	16: 'd-mmm',
	17: 'mmm-yy',
	18: 'h:mm AM/PM',
	19: 'h:mm:ss AM/PM',
	20: 'h:mm',
	21: 'h:mm:ss',
	22: 'm/d/yy h:mm',
	45: 'mm:ss',
	46: '[h]:mm:ss',
	47: 'mmss.0'
};

CalendarDate.oSymbols = {
	'Y': {
		apply: function(sValue, oDate) {
			oDate.setUTCFullYear(sValue);
		}
	},
	'M': {
		apply: function(sValue, oDate) {
			oDate.setUTCMonth(sValue - 1);
		}
	},
	'D': {
		apply: function(sValue, oDate) {
			oDate.setUTCDate(sValue);
		}
	}
};

/**
 * Creates the Style configuration object for this CalendarDate
 * according to its configuration.
 *
 * @return {Object} - Style descriptor object
 * @protected
 */
CalendarDate.prototype._createConfiguration = function() {
	var config, key, template;

	config = Object.getPrototypeOf(CalendarDate.prototype)._createConfiguration.call(this);

	if (!this.type && !this.format && !this.calendar) {
		config.numberFormat = CalendarDate.DEFAULT.numberFormat;

		return config;
	}

	/* Evaluate type */
	switch (this.type) {
		case 'datetime':
			template = CalendarDate.builtin[22];
			break;
		case 'time':
			template = CalendarDate.builtin[21];
			break;
		default: // Assume 'date' as default
			template = CalendarDate.builtin[14];
	}

	if (typeof this.format === 'string') {
		template = this.format.match(/^[dhmsy\s-,.:/]+(AM\/PM)?$/) ? this.format : template;
	}

	/* Evaluate calendar */
	switch (this.calendar) {
		case 'islamic':
			template = 'b2d mmmm yyyy';
			break;
		case 'japanese':
			template = '[$-ja-JP]ge.m.d';
			break;
		default: // Gregorian calendar is the default case
	}

	config.numberFormat = {
		format: template
	};

	/* Find possible builtin match */
	for (key in CalendarDate.builtin) {
		if (CalendarDate.builtin[key] === template) {
			config.numberFormat = parseInt(key, 10);
		}
	}

	return config;
};

/**
 * Returns the value of the given property that is assigned in the line item.
 *
 * @param {string} sProperty - Name of the property that contains the value
 * @param {Object} oData - Line item that contains data
 * @return {number|null} - XLSX specific date/time format in days since 30.12.1899
 */
CalendarDate.prototype.getValue = function(sProperty, oData) {
	var nTimestamp, value;

	value = oData[sProperty];

	if (value === null || typeof value === 'undefined' || value === '') {
		return null;
	}

	/* Date or String, Number wrapper */
	if (typeof value === 'object') {
		value = value.valueOf();
	}

	/* 1. Unix timestamp as number */
	if (typeof value === 'number') {
		nTimestamp = value;
	}

	/* 2. String based time representation */
	if (typeof value === 'string') {
		nTimestamp = this._parseStringValue(value);
	}

	return typeof nTimestamp === 'number' ? CalendarDate._JsDateToExcel(nTimestamp, this.type === 'time') : null;
};

/**
 * Converts a textual date/time representation to a numeric timestamp.
 *
 * @param {string} sValue - String representation of the date/time
 * @return {number|null} - Numeric timestamp
 * @private
 */
CalendarDate.prototype._parseStringValue = function(sValue) {
	var bTimeOnly = this.type === 'time';

	/* 1. OData Edm.TimeOfDay */
	if (bTimeOnly && CalendarDate.timeOfDayRegex.test(sValue)) {
		return CalendarDate._parseTimeOfDay(sValue);
	}

	/* 2. OData duration string 'PT01H23M45S' */
	if (bTimeOnly && CalendarDate.odataDurationRegex.test(sValue)) {
		return CalendarDate._parseODataDuration(sValue);
	}

	/* 3. Pattern formatted date string */
	if (this.inputFormat && typeof this.inputFormat === 'string') {
		return CalendarDate._parseStringDate(sValue, this.inputFormat);
	}

	/* 4. OData date string '/Date(1234567890)/' */
	if (CalendarDate.odataDateRegex.test(sValue)) {
		return CalendarDate._parseOdataDate(sValue);
	}

	/* 5. DateTimeOffset representation 1986-04-13T01:08:00.000+01:00 */
	if (CalendarDate.odataDateTimeOffsetRegex.test(sValue)) {
		return Date.parse(sValue);
	}

	/* 6. Unix timestamp as string */
	if (typeof nTimestamp !== 'number' && isFinite(sValue)) {
		return parseInt(sValue, 10);
	}

	return null;
};

/** START Copied and adapted from datajs **/
/**
 * Extracts the milliseconds if the value is a date/time instance or formatted string.
 *
 * @param {any} vValue the value (may be <code>undefined</code> or <code>null</code>)
 * @return {any} the number of milliseconds or the value itself
 */
CalendarDate._parseODataDuration = function(vValue) {
	var parts, days, hours, minutes, seconds, ms;
	// / <summary>Parses a string in xsd:duration format.</summary>
	// / <param name="duration" type="String">Duration value.</param>
	// / <remarks>
	// / This method will return the input, if the input string has a year or a month component.
	// / </remarks>
	// / <returns type="Object">Object representing the time</returns>

	parts = CalendarDate.odataDurationRegex.exec(vValue);

	// Unsupported duration value for Edm.Time OData spec (if year of month is present)
	/* EDITED
	if (parts[2] || parts[3]) {
		return vValue;
	}
	 */

	days = parseInt(parts[4] || 0, 10);
	hours = parseInt(parts[5] || 0, 10);
	minutes = parseInt(parts[6] || 0, 10);
	seconds = parseFloat(parts[7] || 0);

	ms = parts[8];
	if (ms) {
		// Parse ms duration value ignoring any precision/scale as that part is anyway ignored by JS Date conversion!
		ms = parseFloat('0.' + ms) * 1000;
	} else {
		ms = 0;
	}

	ms += (seconds * 1000) + (minutes * 60000) + (hours * 3600000) + (days * 86400000);

	if (parts[1] === '-') {
		ms = -ms;
	}

	return ms;
};

CalendarDate._parseTimeOfDay = function(vValue) {
	var parts, hours, minutes, seconds;
	var date = new Date(0);

	parts = CalendarDate.timeOfDayRegex.exec(vValue);
	hours = parseInt(parts[1] || 0, 10);
	minutes = parseInt(parts[2] || 0, 10);
	seconds = parseFloat(parts[3] || 0);
	date.setUTCHours(hours, minutes, seconds);

	return date.getTime();
};

/**
 * Parses an OData date string and returns the corresponding timestamp
 *
 * @param {string} sValue - OData date string i.E. "/Date(1524141637807)/"
 * @return {number} - Unix timestamp according to OData date including offset
 * @private
 */
CalendarDate._parseOdataDate = function(sValue) {
	var matchArray, date, offset;

	/* The RegExp matches up to 4 groups:
		 * 0: Full match
		 * 1: Timestamp (mandatory)
		 * 2: Timezone offset sign [ + | - ] (optional)
		 * 3: Timezone offset minutes [0-9]{2,3} (optional)
		 */
	matchArray = CalendarDate.odataDateRegex.exec(sValue);
	date = new Date(parseInt(matchArray[1], 10));

	if (matchArray[2] && matchArray[3]) {
		offset = parseInt(matchArray[2] + matchArray[3], 10);
		date.setUTCMinutes(date.getUTCMinutes() - offset);
	}

	return date.getTime();
};

/* The regular expression corresponds to something like this:
 * /Date(123+60)/
 *
 * This first number is date ticks, the + may be a - and is optional,
 * with the second number indicating a timezone offset in minutes.
 *
 * On the wire, the leading and trailing forward slashes are
 * escaped without being required to so the chance of collisions is reduced;
 * however, by the time we see the objects, the characters already
 * look like regular forward slashes.
*/
CalendarDate.odataDateRegex = /^\/Date\((-?\d+)(\+|-)?(\d+)?\)\/$/;

// The captured indices for this expression are:
// 0       - complete input
// 1       - direction
// 2,3,4   - years, months, days
// 5,6,7,8 - hours, minutes, seconds, milliseconds
CalendarDate.odataDurationRegex =
	/^([+-])?P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)(?:\.(\d+))?S)?)?/;

/** END Copied and adapted from datajs */

CalendarDate.odataDateTimeOffsetRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2]\d|3[0-1])T([0-1]\d|2[0-3])(:[0-5]\d)(:[0-5]\d(\.\d{1,12})?)?(Z|[+\-](0\d|1[0-2]):[0-5]\d)/;

CalendarDate.dateStringRegex = /([Y]{4,}|[M]{2}|[D]{2})/g;

CalendarDate.timeOfDayRegex = /^([0-1]\d|2[0-3]):([0-5]\d):([0-5]\d)/;

/**
 * Converts a JavaScript Date timestamp to an Office Open XML date
 * representation. Office Open XML date epoch starts at 00.01.1900
 * which is 31.12.1899 and is represented as a number of days since
 * beginning of the epoch.
 *
 * The Javascript Date epoch starts at 01.01.1970 and is represented
 * as a number of milliseconds before or after the beginning of the
 * epoch.
 *
 * The amount of days between 01.01.1900 and 01.01.1970 is exactly
 * 25567. According to the wrong offset in Excel (00.01.1900 instead
 * of 01.01.1900) and the known leap year bug in Excel, we need to
 * add two additional days.
 *
 * https://support.microsoft.com/en-us/help/214326/excel-incorrectly-assumes-that-the-year-1900-is-a-leap-year
 *
 * Time specific information is represented as fraction. This means
 * that the value 30.5 represents 30.5 days after the 31.12.1899
 * where .5 stands for half a day. A day is defined as 24 hours, so
 * half a day means 12 hours since the beginning of the day at
 * 00:00:00. If the integer part of the value is greater than 0
 * date specific information will be present in the cell.
 *
 * This also applies for time formatted cells. Although the cell
 * only displays the time specific part, the editing bar still
 * shows the date specific part. Therefore it is necessary to be
 * able to remove the date specific part from the value in the
 * XLSX file.
 *
 *
 * @param {number} nTimestamp - JavaScript Date timestamp
 * @param {boolean} [bTimeOnly] - Defines whether the date specific part should be kept or removed
 * @return {number} - Amount of days since 31.12.1899
 * @private
 */
CalendarDate._JsDateToExcel = function(nTimestamp, bTimeOnly) {
	var offset, nExcelValue;

	/* Increase the offset if date is after February 28th 1900 */
	offset = nTimestamp >= -2203891200000 ? 2 : 1;

	/**
	 * Fallback if date is before UTC 31.12.1899 00:00:00
	 * which corresponds to 0 in Excel
	 */
	nExcelValue = Math.max((nTimestamp / (86400 * 1000)) + (25567 + offset), 0);

	/* Remove date specific part in case of type time */
	if (bTimeOnly) {
		nExcelValue = nExcelValue % 1;
	}

	return nExcelValue;
};

/**
 * The method parses a given string value according to the provided
 * date pattern.
 *
 * Limitations:
 * Currently only the symbols d, m, y are supported which correspond
 * to day, month, year.
 *
 * @param {string} sValue - Date formatted string
 * @param {string} sPattern - ISO 8601 pattern that gets applied
 * @return {number|null} - UNIX timestamp that represents the parsed date string
 * @private
 */
CalendarDate._parseStringDate = function(sValue, sPattern) {
	var matchArray, date, substring;

	date = new Date(0);

	do {
		matchArray = CalendarDate.dateStringRegex.exec(sPattern);

		if (matchArray !== null) {
			substring = sValue.slice(matchArray.index, matchArray.index + matchArray[0].length);

			CalendarDate.oSymbols[matchArray[0].charAt(0)].apply(parseInt(substring, 10), date);
		}
	} while (matchArray !== null);

	return date.getTime();
};

module.exports = CalendarDate;


//////////////////
// WEBPACK FOOTER
// ./core/xlsx/src/binding/type/CalendarDate.js
// module id = 13
// module chunks = 0